import * as React from 'react'
import { useLocalizeSafe } from '@/modules/shared/context/localize/LocalizeContext'

// Every string of the chart-scan import flow (upload → assign → editor) lives
// under the `chartImport` namespace in src/app/translations/*.resources.json.
// tSafe falls back to the Polish default passed at the call site, so a key that
// is missing from a language file renders Polish instead of a raw key.
export type ChartT = (key: string, defaultValue: string) => string

export function useChartT(): ChartT {
  const { tSafe } = useLocalizeSafe()
  return React.useCallback(
    (key: string, defaultValue: string) => tSafe(`chartImport.${key}`, defaultValue),
    [tSafe],
  )
}

// The upload response says at what enlargement a too-small scan starts to read; show it
// as "about N× too small" so the user can tell a scanner setting from a shrunk file.
export function useUnderscaleFactor(
  scans: { lowResolution?: boolean; lowResolutionFactor?: number | null }[],
): string | null {
  const { activeLanguage } = useLocalizeSafe()
  const factor = scans.find((s) => s.lowResolution)?.lowResolutionFactor
  if (!factor) return null
  return new Intl.NumberFormat(activeLanguage?.code, { maximumFractionDigits: 1 }).format(factor)
}

// {0}, {1}, … substitution — the placeholder convention already used across the
// resource files (see e.g. administrationPanel-companies.*).
export function fmt(template: string, ...args: (string | number)[]): string {
  return template.replace(/\{(\d+)\}/g, (m, i) => {
    const v = args[Number(i)]
    return v === undefined ? m : String(v)
  })
}

// Slavic plural bucket (1 / 2-4 / 5+), matching the one/few/many key triple the
// `import` namespace already uses. Languages with only two forms simply carry
// the same text in the `few` and `many` keys.
export function pluralKey(n: number): 'One' | 'Few' | 'Many' {
  if (n === 1) return 'One'
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'Few'
  return 'Many'
}

// The five activity types are labelled identically in the toolbar, the summary,
// the activity table and the context menu — keep one source of truth so the
// labels can't drift apart between panes.
export const ACTIVITY_LABEL_KEYS: Record<number, { key: string; def: string }> = {
  0: { key: 'actRest', def: 'Odpoczynek' },
  1: { key: 'actAvailability', def: 'Dyspozycja' },
  2: { key: 'actWork', def: 'Praca' },
  3: { key: 'actDriving', def: 'Jazda' },
  4: { key: 'actNoData', def: 'Brak danych' },
}

export function activityLabel(T: ChartT, type: number): string {
  const meta = ACTIVITY_LABEL_KEYS[type]
  return meta ? T(meta.key, meta.def) : '?'
}
